import { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, Filter, Search } from 'lucide-react'

export interface Column<T> {
  key: keyof T
  label: string
  sortable?: boolean
  filterable?: boolean
  render?: (value: any, item: T) => React.ReactNode
  filterType?: 'text' | 'select' | 'date' | 'number'
  filterOptions?: Array<{ value: string; label: string }>
}

interface SortableTableProps<T> {
  data: T[]
  columns: Column<T>[]
  onRowClick?: (item: T) => void
  className?: string
}

export default function SortableTable<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  className = ''
}: SortableTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [filters, setFilters] = useState<Record<keyof T, string>>({} as Record<keyof T, string>)

  const handleSort = (column: keyof T) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const handleFilterChange = (column: keyof T, value: string) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }))
  }

  const filteredAndSortedData = useMemo(() => {
    let result = [...data]

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        result = result.filter(item => {
          const itemValue = item[key]
          if (!itemValue && itemValue !== 0) return false
          if (typeof itemValue === 'string') {
            return itemValue.toLowerCase().includes(value.toLowerCase())
          }
          if (typeof itemValue === 'number') {
            return itemValue.toString().includes(value)
          }
          const stringValue = String(itemValue || '')
          return stringValue.toLowerCase().includes(value.toLowerCase())
        })
      }
    })

    // Apply sorting
    if (sortColumn) {
      result.sort((a, b) => {
        const aValue = a[sortColumn]
        const bValue = b[sortColumn]
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [data, filters, sortColumn, sortDirection])

  const getSortIcon = (column: keyof T) => {
    if (sortColumn !== column) return null
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-1">
                      {column.sortable ? (
                        <button
                          onClick={() => handleSort(column.key)}
                          className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                        >
                          <span>{column.label}</span>
                          {getSortIcon(column.key)}
                        </button>
                      ) : (
                        <span>{column.label}</span>
                      )}
                    </div>
                    
                    {column.filterable && (
                      <div className="relative">
                        {column.filterType === 'select' && column.filterOptions ? (
                          <select
                            value={filters[column.key] || ''}
                            onChange={(e) => handleFilterChange(column.key, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">All</option>
                            {column.filterOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="relative">
                            <input
                              type={column.filterType === 'number' ? 'number' : 'text'}
                              value={filters[column.key] || ''}
                              onChange={(e) => handleFilterChange(column.key, e.target.value)}
                              placeholder={`Filter ${column.label.toLowerCase()}...`}
                              className="text-xs border border-gray-300 rounded px-2 py-1 pl-6 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                            />
                            <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedData.map((item, index) => (
              <tr
                key={item.id || index}
                onClick={() => onRowClick?.(item)}
                className={`${onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''} transition-colors`}
              >
                {columns.map((column) => (
                  <td key={String(column.key)} className="px-6 py-4 whitespace-nowrap text-sm">
                    {column.render ? 
                      column.render(item[column.key], item) : 
                      typeof item[column.key] === 'object' 
                        ? JSON.stringify(item[column.key] || {})
                        : String(item[column.key] || '')
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No data matches the current filters</p>
          </div>
        )}
      </div>
    </div>
  )
}